import { useEffect, useState } from 'react';
import { workerHistoryService, type PositionHistory } from '@/services/api/workerHistoryService';

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

type Item =
  | { kind: 'incumbent'; from: string | null; to: string | null; pn: string; current: boolean; reason: string | null }
  | { kind: 'vacancy'; from: string | null; to: string | null; days: number };

export default function PositionHistoryTimeline({ foPositionId }: { foPositionId: string }) {
  const [data, setData] = useState<PositionHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null); setData(null);
    workerHistoryService.getPositionHistory(foPositionId)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(e?.response?.data?.detail || e?.message || 'Failed to load'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [foPositionId]);

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading position history…</div>;
  if (error) return <div className="p-6 text-sm text-rose-600">{error}</div>;
  if (!data) return null;

  // Interleave incumbents + vacancy periods chronologically.
  const items: Item[] = [
    ...data.incumbents.map((i): Item => ({ kind: 'incumbent', from: i.valid_from, to: i.valid_to, pn: i.personnel_number, current: i.is_current, reason: i.reason_code })),
    ...data.vacancy_periods.map((v): Item => ({ kind: 'vacancy', from: v.from, to: v.to, days: v.days })),
  ].sort((a, b) => (a.from || '').localeCompare(b.from || ''));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-sm text-heritage-blue">{data.fo_position_id}</span>
        <h2 className="font-serif text-2xl text-heritage-blue">{data.title || 'Position'}</h2>
        {data.currently_vacant
          ? <span className="text-xs font-medium px-2 py-0.5 rounded-full ring-1 bg-rose-50 text-rose-700 ring-rose-200">Vacant now</span>
          : <span className="text-xs font-medium px-2 py-0.5 rounded-full ring-1 bg-emerald-50 text-emerald-700 ring-emerald-200">Filled</span>}
        <span className="text-xs text-slate-400 tabular-nums">{data.incumbents.length} incumbents · {data.vacancy_periods.length} vacancies</span>
      </div>

      <div className="space-y-1.5">
        {items.map((it, i) =>
          it.kind === 'incumbent' ? (
            <div key={i} className={`flex flex-wrap items-center gap-3 text-sm rounded-lg px-3 py-2 ring-1 ${it.current ? 'bg-heritage-blue/5 ring-heritage-blue/20' : 'bg-white ring-slate-100'}`}>
              <span className="font-mono text-xs text-heritage-blue">PN {it.pn}</span>
              <span className="tabular-nums text-slate-700">{fmt(it.from)} → {it.to ? fmt(it.to) : 'present'}</span>
              {it.reason && <span className="text-xs text-slate-400">{it.reason}</span>}
              {it.current && <span className="text-xs text-heritage-blue">current</span>}
            </div>
          ) : (
            <div key={i} className="flex flex-wrap items-center gap-3 text-sm rounded-lg px-3 py-2 ring-1 bg-rose-50/40 ring-rose-100 border-l-2 border-rose-300">
              <span className="text-xs font-medium text-rose-600">Vacant</span>
              <span className="tabular-nums text-rose-700/80">{fmt(it.from)} → {fmt(it.to)}</span>
              <span className="text-xs text-rose-400 tabular-nums">{it.days} days</span>
            </div>
          )
        )}
      </div>
    </div>
  );
}
